/**
 * Live-only execution cards (`exec.started/output/result` over the E2E relay).
 * Content is rendered directly (masking was removed by user request, task-31);
 * long output is truncated by the reducer before it ever reaches the DOM.
 */
import { formatDuration, formatRelative } from '../lib/format';
import { EXEC_STATUS_LABELS, EXEC_STATUS_TONES, type ExecView } from '../lib/live/reducers';
import { Icon } from './Icon';
import { MaskedText } from './MaskedText';

function ExecCard({ exec, now }: { exec: ExecView; now: number }) {
  const hasOutput = exec.stdout.length > 0 || exec.stderr.length > 0;
  return (
    <article className="rounded-2xl border border-white/10 bg-[#0f1629] p-3.5 text-xs">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <code className="truncate rounded bg-black/30 px-2 py-0.5 font-mono text-[11px] text-indigo-200">
              {exec.tool}
            </code>
            <span
              className={`rounded-full border px-2 py-0.5 text-[10px] ${EXEC_STATUS_TONES[exec.status]}`}
            >
              {EXEC_STATUS_LABELS[exec.status]}
            </span>
            {exec.pid !== undefined ? (
              <span className="text-[10px] text-slate-500">pid {exec.pid}</span>
            ) : null}
            {exec.exit_code !== undefined ? (
              <span className="text-[10px] text-slate-500">exit {exec.exit_code ?? '—'}</span>
            ) : null}
            {exec.duration_ms !== undefined ? (
              <span className="text-[10px] text-slate-500">
                耗时 {formatDuration(exec.duration_ms)}
              </span>
            ) : null}
          </div>
          <p className="mt-1 font-mono text-[10px] text-slate-500">
            exec {exec.exec_id} · 更新于 {formatRelative(exec.updated_at, now)}
          </p>
        </div>
        <Icon name="sessions" className="h-4 w-4 shrink-0 text-slate-600" />
      </header>
      {hasOutput ? (
        <details className="mt-2 rounded-xl border border-white/5 bg-black/25 px-2.5 py-2">
          <summary className="cursor-pointer text-[11px] text-slate-400">
            执行输出{exec.truncated ? '（已截断，仅保留末尾）' : ''}
          </summary>
          {exec.stdout ? (
            <div className="mt-1.5 text-slate-200">
              <MaskedText value={exec.stdout} multiline />
            </div>
          ) : null}
          {exec.stderr ? (
            <div className="mt-1.5 text-rose-200/80">
              <MaskedText value={exec.stderr} multiline />
            </div>
          ) : null}
        </details>
      ) : (
        <p className="mt-2 text-[11px] text-slate-500">暂无输出</p>
      )}
    </article>
  );
}

export function ExecPanel({ execs, now }: { execs: readonly ExecView[]; now: number }) {
  if (execs.length === 0) return null;
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <h2 className="text-xs font-medium text-slate-300">实时执行</h2>
        <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-400">
          {execs.length}
        </span>
        <span className="h-px flex-1 bg-white/5" />
      </div>
      <p className="px-1 text-[10px] text-slate-500">
        live relay：执行状态与输出经 E2E 中继推送；输出直接展示、超长自动截断。
      </p>
      {execs.map((exec) => (
        <ExecCard key={exec.exec_id} exec={exec} now={now} />
      ))}
    </section>
  );
}
